'use client';

import { Session } from 'next-auth';
import { useRouter, useSearchParams } from 'next/navigation';
import { useState } from 'react';

import { FUNDING_ENUM } from '@bytel/magicart/dist/src/interfaces';
import { IconName } from '@bytel/trilogy-assets/lib/iconNameEnum';

import { getSessionVariable, setSessionVariable } from '@services/storage';

import { CategoryListType, CategoryPlanType, MobilePlanType } from '@app-types/plan';
import { Product } from '@app-types/product';
import { UserType } from '@app-types/user';

import { ProductRedirectModal, RedirectionOptionsType } from '@components/product-redirect-modal';
import { TriggerLogin } from '@components/trigger-login';

import { useMarketings } from '@hooks/use-marketings';

import { signIn } from '@helpers/authentication';
import { getCart } from '@helpers/cart';
import { getClientCookies } from '@helpers/cookies-client';
import { getSessionFunding } from '@helpers/funding';
import { paths } from '@helpers/path';
import { LockedPlanValue, getBlockedPlans } from '@helpers/plan-lock';
import { getNeededElementGencodes } from '@helpers/product';

import { OPTION_LOCKED_ID, PlanRange, PlanSubTypeId } from '@constants/plan';

import { OfferWallAcquisitionContainer } from '@home-mobile/components';
import { sendPlanDetailsTracking } from '@home-mobile/helpers';

import { ModalPlanDetailsContainer } from './modal-plan-details-container';

type OfferWallContainerProps = {
    categoryList: CategoryListType;
    categoryListToDisplay: CategoryPlanType[];
    session: Session | null;
    contractId?: string;
};

export function OfferWallContainer({
    categoryList,
    categoryListToDisplay,
    session,
    contractId,
}: Readonly<OfferWallContainerProps>) {
    const router = useRouter();
    const [offerSelectedForDetails, setOfferSelectedForDetails] = useState<MobilePlanType>();
    const [marketingDiscounts, setMarketingDiscounts] = useMarketings();
    // TODO: phoneInCart seems to have a strange type declaration in the child component (offer-wall-renewal-container.tsx & offer-wall-acquisition-container.tsx)
    const [phoneInCart, setPhoneInCart] = useState<Product>();
    const searchParams = useSearchParams();
    const openOfferDetails = (offer: MobilePlanType) => {
        sendPlanDetailsTracking(offer);
        setOfferSelectedForDetails(offer);
    };

    const [redirectUrls, setRedirectUrls] = useState<RedirectionOptionsType>();

    const user = session?.user as UserType | undefined;

    const onAddToCart = async (selectedOffer: MobilePlanType, fundingKey?: string) => {
        setOfferSelectedForDetails(undefined);
        const cart = await getCart(user, contractId, false);

        const autoAdd = getNeededElementGencodes(selectedOffer);

        // Handle locked plans option
        if (selectedOffer.locked === LockedPlanValue.ONE) {
            const blockedsPlans = getBlockedPlans();
            if (blockedsPlans[selectedOffer.entityId!]) {
                autoAdd.push(OPTION_LOCKED_ID);
            }
        }
        await cart
            .addProductsToCart(
                phoneInCart
                    ? [selectedOffer.gencode, phoneInCart.gencode, ...autoAdd]
                    : [selectedOffer.gencode, ...autoAdd],
            )
            .then(async () => {
                const funding = fundingKey ?? getSessionFunding();
                if (funding) await cart.setFundingMethod(funding as FUNDING_ENUM);
            });

        const shouldSkipCrossSell = getClientCookies('skipFsmCrossSell');

        let redirectionPath = paths.phoneWall;

        if (selectedOffer.subTypeId === PlanSubTypeId.SMART_CHANGE) {
            redirectionPath = paths.phoneFilter(false, 'smart-change');
        }

        if (
            selectedOffer.commercialRange === PlanRange.WITHOUT_MOBILE ||
            (selectedOffer.commercialRange === PlanRange.WITH_MOBILE && !!phoneInCart)
        ) {
            redirectionPath = shouldSkipCrossSell ? paths.cart : paths.crossSell();
        }

        if (getSessionVariable('hasSelectedOfferWallRedirection')) {
            router.push(redirectionPath);
        } else {
            setRedirectUrls({
                acquisition: { url: redirectionPath },
                renewal: { url: '', cta: 'Se connecter', icon: IconName.BOX },
            });
        }
    };

    const isWithSmartphone = searchParams.has('smartphone') ? searchParams.get('smartphone') === 'true' : true;

    const setHasSelectedWallRedirection = (shouldRedirect?: boolean) => {
        setRedirectUrls(undefined);
        setSessionVariable('hasSelectedOfferWallRedirection', 'true');
        if (shouldRedirect) {
            void signIn(isWithSmartphone ? paths.phoneWall : paths.cart);
        }
    };

    return (
        <>
            <OfferWallAcquisitionContainer
                categoryList={categoryList}
                categoryListToDisplay={categoryListToDisplay}
                marketingDiscounts={marketingDiscounts}
                setMarketingDiscounts={setMarketingDiscounts}
                isWithSmartphone={isWithSmartphone}
                openOfferDetails={openOfferDetails}
                phoneInCart={phoneInCart}
                setPhoneInCart={setPhoneInCart}
                onAddToCart={onAddToCart}
                session={session}
            />
            {offerSelectedForDetails && (
                <ModalPlanDetailsContainer
                    offerSelectedForDetails={offerSelectedForDetails}
                    marketingDiscounts={marketingDiscounts}
                    onClose={() => setOfferSelectedForDetails(undefined)}
                    onAddToCart={onAddToCart}
                    phoneInCart={phoneInCart}
                />
            )}
            <TriggerLogin />
            {redirectUrls && <ProductRedirectModal onAction={setHasSelectedWallRedirection} options={redirectUrls} />}
        </>
    );
}
